import React, { useState, useRef, useEffect } from 'react';
import { Send, Bot, User, Sparkles, ThumbsUp, ThumbsDown, RotateCcw, TrendingUp, Search } from 'lucide-react';
import { faqMatcher } from '../utils/faqMatcher';
import { Button } from './ui/button';

const FAQChatInterface = () => {
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'bot',
      text: "Hi! 👋 I'm your AI assistant. I can answer questions about our consulting services, pricing, processes, and more. What would you like to know?",
      suggestions: [],
      timestamp: new Date()
    }
  ]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);

  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Handle input change with real-time suggestions
  const handleInputChange = (e) => {
    const value = e.target.value;
    setInput(value);

    // Show suggestions when user types 2+ characters
    if (value.trim().length >= 2) {
      const matches = faqMatcher.findMatchingQuestions(value, 6);
      setSuggestions(matches);
      setShowSuggestions(matches.length > 0);
    } else {
      setShowSuggestions(false);
      setSuggestions([]);
    }
  };

  // Handle sending message
  const handleSend = () => {
    if (!input.trim()) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      text: input,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setShowSuggestions(false);
    setSuggestions([]);
    setIsTyping(true);

    // Simulate typing delay for better UX
    setTimeout(() => {
      const result = faqMatcher.findBestMatch(userMessage.text);
      
      const botMessage = {
        id: Date.now() + 1,
        type: 'bot',
        text: result.success ? result.answer : result.message,
        question: result.question,
        confidence: result.confidence,
        suggestions: result.suggestions || result.relatedQuestions || [],
        cta: result.cta,
        fallback: result.fallback,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, botMessage]);
      setIsTyping(false);
    }, 800);
  };

  // Handle dropdown suggestion click
  const handleDropdownSuggestionClick = (suggestion) => {
    setInput(suggestion);
    setShowSuggestions(false);
    setSuggestions([]);
    // Auto-send after selecting suggestion
    setTimeout(() => {
      const userMessage = {
        id: Date.now(),
        type: 'user',
        text: suggestion,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, userMessage]);
      setInput('');
      setIsTyping(true);

      setTimeout(() => {
        const result = faqMatcher.findBestMatch(suggestion);
        
        const botMessage = {
          id: Date.now() + 1,
          type: 'bot',
          text: result.success ? result.answer : result.message,
          question: result.question,
          confidence: result.confidence,
          suggestions: result.suggestions || result.relatedQuestions || [],
          cta: result.cta,
          fallback: result.fallback,
          timestamp: new Date()
        };

        setMessages(prev => [...prev, botMessage]);
        setIsTyping(false);
      }, 800);
    }, 100);
  };

  // Handle suggestion click (from message suggestions)
  const handleSuggestionClick = (suggestion) => {
    setInput(suggestion);
    setTimeout(() => {
      handleSend();
    }, 100);
  };

  // Handle key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  // Click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target) &&
        !inputRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Clear chat
  const handleClearChat = () => {
    setMessages([
      {
        id: 1,
        type: 'bot',
        text: "Chat cleared! What would you like to know?",
        suggestions: [],
        timestamp: new Date()
      }
    ]);
  };

  // Handle feedback
  const handleFeedback = (messageId, isHelpful) => {
    console.log(`Message ${messageId} was ${isHelpful ? 'helpful' : 'not helpful'}`);
    // You can add analytics tracking here
  };

  return (
    <div className="faq-chat-container">
      {/* Header */}
      <div className="chat-header">
        <div className="flex items-center gap-3">
          <div className="bot-avatar">
            <Sparkles size={20} className="text-turquoise-400" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-white">Ask Me Anything</h3>
            <p className="text-sm text-gray-400">Powered by AI • Instant answers</p>
          </div>
        </div>
        <button 
          onClick={handleClearChat}
          className="clear-btn"
          title="Clear chat"
        >
          <RotateCcw size={18} />
        </button>
      </div>

      {/* Messages Area */}
      <div className="messages-area">
        {messages.map((message) => (
          <div key={message.id} className={`message ${message.type}`}>
            {/* Avatar */}
            <div className={`message-avatar ${message.type}`}>
              {message.type === 'bot' ? (
                <Bot size={20} />
              ) : (
                <User size={20} />
              )}
            </div>

            {/* Content */}
            <div className="message-content">
              {/* Show matched question if available */}
              {message.question && (
                <div className="matched-question">
                  <TrendingUp size={14} className="text-turquoise-400" />
                  <span>Matched: {message.question}</span>
                  {message.confidence && (
                    <span className="confidence-badge">
                      {message.confidence}% match
                    </span>
                  )}
                </div>
              )}

              {/* Message text */}
              <div className="message-text">
                {message.text}
              </div>

              {/* CTA Button */}
              {message.cta && (
                <Button
                  className="btn-primary mt-3"
                  onClick={() => {
                    if (message.cta.link.startsWith('http')) {
                      window.open(message.cta.link, '_blank');
                    } else {
                      window.location.href = message.cta.link;
                    }
                  }}
                >
                  {message.cta.text}
                </Button>
              )}

              {/* Suggestions */}
              {message.suggestions && message.suggestions.length > 0 && (
                <div className="suggestions-container">
                  <p className="suggestions-label">
                    {message.fallback ? "Try asking:" : "Related questions:"}
                  </p>
                  <div className="suggestions-grid">
                    {message.suggestions.slice(0, 4).map((suggestion, idx) => (
                      <button
                        key={idx}
                        className="suggestion-btn"
                        onClick={() => handleSuggestionClick(suggestion)}
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Feedback buttons (only for bot messages) */}
              {message.type === 'bot' && message.id !== 1 && (
                <div className="feedback-buttons">
                  <button
                    onClick={() => handleFeedback(message.id, true)}
                    className="feedback-btn"
                    title="Helpful"
                  >
                    <ThumbsUp size={14} />
                  </button>
                  <button
                    onClick={() => handleFeedback(message.id, false)}
                    className="feedback-btn"
                    title="Not helpful"
                  >
                    <ThumbsDown size={14} />
                  </button>
                </div>
              )}

              {/* Timestamp */}
              <div className="message-timestamp">
                {message.timestamp.toLocaleTimeString('en-US', { 
                  hour: '2-digit', 
                  minute: '2-digit' 
                })}
              </div>
            </div>
          </div>
        ))}

        {/* Typing Indicator */}
        {isTyping && (
          <div className="message bot">
            <div className="message-avatar bot">
              <Bot size={20} />
            </div>
            <div className="typing-indicator">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="input-area">
        <div className="input-wrapper" style={{ position: 'relative' }}>
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
            placeholder="Type your question here..."
            className="chat-input"
            autoComplete="off"
          />
          <button
            onClick={handleSend}
            disabled={!input.trim()}
            className="send-btn"
            title="Send message"
          >
            <Send size={20} />
          </button>

          {/* Suggestions Dropdown */}
          {showSuggestions && suggestions.length > 0 && (
            <div 
              ref={suggestionsRef}
              className="suggestions-dropdown"
            >
              {suggestions.map((suggestion, index) => (
                <button
                  key={index}
                  onClick={() => handleDropdownSuggestionClick(suggestion.question)}
                  className="suggestion-dropdown-item group"
                >
                  <Search className="suggestion-icon" />
                  <div className="suggestion-content">
                    <p className="suggestion-question">
                      {suggestion.question}
                    </p>
                    <div className="suggestion-meta">
                      <span className="suggestion-category">
                        {suggestion.categoryDisplay}
                      </span>
                      <span className="suggestion-score">
                        {suggestion.score}% match
                      </span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="input-footer">
          <span className="text-xs text-gray-500">
            💡 Pro tip: Ask about pricing, services, or how to get started
          </span>
        </div>
      </div>
    </div>
  );
};

export default FAQChatInterface;
